import CategoryFilter from "../../components/common/CategoryFilter";
import CategorySidebar from "../../components/common/CategorySidebar";
import SearchBar from "../../components/common/SearchBar";
import UseCaseCard from "../../components/home/UseCaseCard";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { useMemo, useState, useEffect, useCallback } from "react";
import useAgentList from "../../hooks/agent/useAgentList";
import useAgentSearch from "../../hooks/agent/useAgentSearch";
import useAgentCategories from "../../hooks/agent/useAgentCategories";
import { useInfiniteScroll } from "../../hooks/common/useInfiniteScroll";
import Footer from "../../components/ui/Footer";
import Header from "../../components/ui/Header";

const mapAgentKindToCategory = (agentKind: string): string => {
  switch (agentKind) {
    case "text-modal":
      return "교육";
    case "image-modal":
    case "gen-image-modal":
      return "창작 활동";
    case "emotion-modal":
      return "정서/심리";
    case "wellness-modal":
      return "웰니스";
    default:
      return "기타";
  }
};

export default function AgentList() {
  const [selectedCategory, setSelectedCategory] = useState("전체");
  const [showEndMessage, setShowEndMessage] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);

  const {
    categories: apiCategories,
    isLoading: isCategoriesLoading,
    error: categoriesError,
  } = useAgentCategories();

  const {
    agents,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
    refresh,
  } = useAgentList({
    category: selectedCategory === "전체" ? "all" : selectedCategory,
  });

  const {
    searchResults,
    isLoading: isSearchLoading,
    isLoadingMore: isSearchLoadingMore,
    error: searchError,
    hasMore: hasMoreSearch,
    searchAgents,
    loadMore: loadMoreSearch,
    clearSearch,
    currentQuery,
  } = useAgentSearch({
    category: selectedCategory === "전체" ? "all" : selectedCategory,
  });

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setShowEndMessage(false);
    // 카테고리 변경 시 검색 초기화 (무한 요청 방지)
    if (isSearchMode) {
      clearSearch();
      setIsSearchMode(false);
    }
  };

  useEffect(() => {
    if (!isSearchMode) {
      refresh();
    }
  }, [selectedCategory, isSearchMode, refresh]);

  const handleSearch = useCallback(
    async (query: string) => {
      setIsSearchMode(true);
      setShowEndMessage(false);
      await searchAgents(query);
    },
    [searchAgents]
  );

  const handleClearSearch = useCallback(() => {
    setIsSearchMode(false);
    setShowEndMessage(false);
    clearSearch();
  }, [clearSearch]);

  const currentAgents = isSearchMode ? searchResults : agents;
  const currentIsLoading = isSearchMode ? isSearchLoading : isLoading;
  const currentIsLoadingMore = isSearchMode
    ? isSearchLoadingMore
    : isLoadingMore;
  const currentError = isSearchMode ? searchError : error;
  const currentHasMore = isSearchMode ? hasMoreSearch : hasMore;

  // 무한 스크롤 설정 
  useInfiniteScroll({
    hasMore: isSearchMode ? hasMoreSearch : hasMore,
    isLoading: isSearchMode ? isSearchLoadingMore : isLoadingMore,
    loadMore: isSearchMode ? loadMoreSearch : loadMore,
    threshold: 200,
    enabled: !currentIsLoading && !currentIsLoadingMore && !isSearchLoading,
  });

  const { filteredAgents, categoriesWithCount, categories } = useMemo(() => {
    const baseCategories = [
      { id: "all", label: "전체" },
      ...apiCategories.map((category, index) => ({
        id: category.toLowerCase().replace(/\s+/g, "-"),
        label: category,
      })),
    ];

    // 계층 구조 생성
    const hierarchicalCategories = [
      {
        id: "all",
        label: "전체",
        count: 0,
      },
      ...apiCategories.map((category, index) => ({
        id: category.toLowerCase().replace(/\s+/g, "-"),
        label: category,
        count: 0,
      })),
    ];

    // 검색 모드일 때는 검색 결과를 그대로 사용
    if (isSearchMode) {
      return {
        filteredAgents: currentAgents || [],
        categoriesWithCount: hierarchicalCategories,
        categories: baseCategories,
      };
    }

    if (!currentAgents || currentAgents.length === 0) {
      return {
        filteredAgents: [],
        categoriesWithCount: hierarchicalCategories,
        categories: baseCategories,
      };
    }

    const mappedAgents = currentAgents.map((agent) => ({
      ...agent,
      category: mapAgentKindToCategory(agent.agent_kind),
    }));

    // 현재 선택된 카테고리의 에이전트 개수
    const currentCount = mappedAgents.length;

    // 계층 구조 업데이트
    const updatedHierarchicalCategories = hierarchicalCategories.map(
      (category) => {
        let mainCount = 0;

        if (category.id === "all") {
          // 전체 카테고리는 현재 로드된 모든 에이전트 개수
          mainCount = currentCount;
        } else {
          // 개별 카테고리는 현재 선택된 카테고리의 에이전트 개수
          if (selectedCategory === category.label) {
            mainCount = currentCount;
          } else {
            mainCount = 0;
          }
        }

        return {
          ...category,
          count: mainCount,
        };
      }
    );

    let filtered = mappedAgents;

    return {
      filteredAgents: filtered,
      categoriesWithCount: updatedHierarchicalCategories,
      categories: baseCategories,
    };
  }, [currentAgents, selectedCategory, isSearchMode, apiCategories]);

  useEffect(() => {
    if (!currentHasMore && filteredAgents.length > 0 && !currentIsLoading) {
      setShowEndMessage(true);

      const timer = setTimeout(() => {
        setShowEndMessage(false);
      }, 1000);

      return () => clearTimeout(timer);
    } else {
      setShowEndMessage(false);
    }
  }, [currentHasMore, filteredAgents.length, currentIsLoading]);

  return (
    <>
      <Header />
      <main className="mx-auto py-[64px] md:py-[100px] px-5 relative max-w-7xl">
        {!isCategoriesLoading && categoriesWithCount.length > 0 && (
          <CategorySidebar
            categories={categoriesWithCount}
            selectedCategory={selectedCategory}
            onCategoryChange={handleCategoryChange}
          />
        )}

        <section
          id="case"
          className="flex flex-col items-center mx-auto rounded-[64px] w-full max-md:bg-transparent max-md:gap-[30px] gap-[40px]"
        >
          <div className="flex flex-col justify-center items-center gap-[40px] max-md:gap-[16px] self-stretch flex-wrap">
            <header className="text-center">
              <h2 className="text-black text-[32px] md:text-5xl font-medium leading-[140%] font-serif-kr">
                {isSearchMode && currentQuery
                  ? `'${currentQuery}' 검색 결과`
                  : "에이전트 갤러리"}
              </h2>
              <p className="max-w-[672px] text-base text-gray-400 md:text-[20px] leading-[25px] mt-[20px] mx-auto font-serif-kr whitespace-pre-line md:whitespace-nowrap">
                {isSearchMode && currentQuery
                  ? `${searchResults.length}개의 에이전트를 찾았습니다.`
                  : "단계별 재생을 통해 Aivy가 실제 작업을\n처리하는 방법을 알아보세요."}
              </p>
            </header>

            {/* 검색 바 */}
            <div className="w-full max-w-2xl mx-auto">
              <SearchBar
                onSearch={handleSearch}
                onClear={handleClearSearch}
                isLoading={isSearchLoading}
                className="w-full"
              />
            </div>

            {/* 카테고리 필터 */}
            {!isSearchMode && !isCategoriesLoading && categories.length > 0 && (
              <nav className="w-full">
                <CategoryFilter
                  categories={categories}
                  selectedCategory={selectedCategory}
                  onCategoryChange={handleCategoryChange}
                />
              </nav>
            )}
          </div>

          <div className="w-full">
            {isCategoriesLoading ? (
              <LoadingSpinner message="카테고리를 불러오는 중" size="lg" />
            ) : categoriesError ? (
              <div className="text-center py-12">
                <p className="text-red-600 text-lg">{categoriesError}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-4 px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
                >
                  다시 시도
                </button>
              </div>
            ) : currentIsLoading ? (
              <LoadingSpinner
                message={isSearchMode ? "검색 중..." : "에이전트를 불러오는 중"}
                size="lg"
              />
            ) : currentError ? (
              <div className="text-center py-12">
                <p className="text-red-600 text-lg">{currentError}</p>
                <button
                  onClick={
                    isSearchMode ? () => handleSearch(currentQuery) : refresh
                  }
                  className="mt-4 px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
                >
                  다시 시도
                </button>
              </div>
            ) : (
              <>
                {/* 에이전트 카드 */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {isSearchMode
                    ? searchResults.map((agent) => (
                        <UseCaseCard
                          key={agent.agent_id}
                          agent_id={agent.agent_id}
                          title={agent.agent_title}
                          image={agent.agent_image}
                        />
                      ))
                    : filteredAgents.map((agent) => (
                        <UseCaseCard
                          key={agent.agent_id}
                          agent_id={agent.agent_id}
                          title={agent.agent_title}
                          image={agent.agent_image}
                        />
                      ))}
                </div>

                {/* 더 많은 에이전트 로딩 메시지 */}
                {currentIsLoadingMore && (
                  <div className="flex justify-center py-8">
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-teal-500 border-t-transparent"></div>
                      <span className="text-gray-600">
                        {isSearchMode
                          ? `더 많은 검색 결과를 불러오는 중...`
                          : "더 많은 에이전트를 불러오는 중..."}
                      </span>
                    </div>
                  </div>
                )}

                {/* 모든 에이전트 로딩 완료 메시지 */}
                {showEndMessage && (
                  <div className="text-center py-8 transition-opacity duration-500">
                    <p className="text-gray-500">
                      {isSearchMode
                        ? `모든 검색 결과를 불러왔습니다.`
                        : "모든 에이전트를 불러왔습니다."}
                    </p>
                  </div>
                )}
              </>
            )}

            {/* 검색 결과가 없을 때 */}
            {!currentIsLoading &&
              !currentError &&
              (isSearchMode && currentQuery
                ? searchResults.length === 0 && !isSearchLoading
                : filteredAgents.length === 0) && (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">
                    {isSearchMode && currentQuery
                      ? `'${currentQuery}'에 대한 검색 결과가 없습니다.`
                      : "선택한 카테고리에 해당하는 에이전트가 없습니다."}
                  </p>
                  {/* 전체 에이전트 보기 버튼 */}
                  {isSearchMode && currentQuery && (
                    <button
                      onClick={handleClearSearch}
                      className="mt-4 px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      전체 에이전트 보기
                    </button>
                  )}
                </div>
              )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
